import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { VoiceNote } from 'lib/redux/linkedin/voice-memos/slice';
import { LinkedInVoiceMemoMetadata } from 'interfaces/db';
import { VoiceNoteTile } from './VoiceNoteTile';
import {
  Grid,
  Box,
  Typography,
  Button,
  CircularProgress
} from '@mui/material';
import {
  InsertEmoticon as InsertEmoticonIcon,
  Close as CloseIcon
} from '@mui/icons-material';

interface VoiceNoteListProps {
  voiceNotes: VoiceNote[] | null;
  filteredVoiceNotes: VoiceNote[] | null;
  userId: string | undefined;
  currentlyPlaying: string | null;
  loadingStates: Record<string, boolean>;
  searchTerm: string;
  onClearSearch: () => void;
  onPlayVoiceNote: (id: string) => void;
  onEditVoiceNote: (voiceNote: LinkedInVoiceMemoMetadata) => void;
  onOpenActionMenu: (event: React.MouseEvent<HTMLButtonElement>, voiceNoteId: string) => void;
  onDeleteVoiceNote: (voiceNoteId: string, skipConfirmation?: boolean) => void;
}

export const VoiceNoteList: React.FC<VoiceNoteListProps> = ({
  voiceNotes,
  filteredVoiceNotes,
  userId,
  currentlyPlaying,
  loadingStates,
  searchTerm,
  onClearSearch,
  onPlayVoiceNote,
  onEditVoiceNote,
  onOpenActionMenu,
  onDeleteVoiceNote,
}) => {
  if (voiceNotes === null) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3, 4, 5, 6].map((key) => (
          <Grid item xs={12} sm={6} md={4} key={key}>
            <Box
              sx={{
                height: '100%',
                borderRadius: '12px',
                border: '1px solid rgb(229, 231, 235)',
                p: 2.5,
              }}
            >
              <Box sx={{ 
                width: '60%', 
                height: 24, 
                backgroundColor: 'rgba(0, 0, 0, 0.04)', 
                mb: 2,
                borderRadius: '4px'
              }} />
              <Box sx={{ 
                width: '40%', 
                height: 16, 
                backgroundColor: 'rgba(0, 0, 0, 0.04)', 
                mb: 3,
                borderRadius: '4px'
              }} />
              <Box sx={{ 
                width: '100%', 
                height: 48, 
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: '6px'
              }} />
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!filteredVoiceNotes || filteredVoiceNotes.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          p: 4,
          bgcolor: 'white',
          borderRadius: '12px',
          border: '1px solid rgb(229, 231, 235)'
        }}
      >
        <InsertEmoticonIcon sx={{ fontSize: 48, color: 'rgb(156, 163, 175)', mb: 2 }} />
        <Typography 
          variant="h5" 
          sx={{ 
            color: 'rgb(17, 24, 39)',
            mb: 1,
            fontWeight: 600
          }}
        >
          {searchTerm ? 'No voice notes match your search' : 'No voice notes found'}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'rgb(107, 114, 128)',
            mb: 3
          }}
        >
          {searchTerm 
            ? 'Try a different search term' 
            : 'Create your first voice note by clicking the "New Voice Note" button'}
        </Typography>
        {searchTerm && (
          <Button
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={onClearSearch}
            sx={{ 
              borderRadius: '6px',
              textTransform: 'none',
              borderColor: 'rgb(229, 231, 235)',
              color: 'rgb(75, 85, 99)',
              '&:hover': {
                borderColor: 'rgb(107, 114, 128)',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Clear Search
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {filteredVoiceNotes.map((voiceNote) => (
        <Grid item xs={12} sm={6} md={4} key={voiceNote.linkedin_voice_memo_id}>
          <VoiceNoteTile
            voiceNote={voiceNote}
            userId={userId}
            currentlyPlaying={currentlyPlaying}
            loadingStates={loadingStates}
            onPlay={onPlayVoiceNote}
            onEdit={onEditVoiceNote}
            onOpenActionMenu={onOpenActionMenu}
            onDelete={onDeleteVoiceNote}
          />
        </Grid>
      ))}
    </Grid>
  );
}; 